import { createApiClient } from '@lista/api-client';
import type { LoginResponse } from '@lista/shared-validation';
import i18n from 'i18next';
import { getAccessToken, useSession } from '../auth/session-store';
import { setListPreviewCount } from '@lista/app-core';
import { API_BASE_URL, LIST_PREVIEW_ITEMS } from './config';

setListPreviewCount(LIST_PREVIEW_ITEMS);

/**
 * Refresh with the HttpOnly cookie. Uses plain fetch so it never re-enters the 401
 * middleware; the custom header + same origin satisfy the API's CSRF checks.
 */
export async function refreshSession(): Promise<LoginResponse | null> {
  return (await tryRefresh()).session;
}

/** Distinguishes "signed out" from "can't reach the server" (offline start-up). */
export async function tryRefresh(): Promise<{ session: LoginResponse | null; offline: boolean }> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Requested-With': 'lista', 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!res.ok) return { session: null, offline: res.status >= 500 };
    return { session: (await res.json()) as LoginResponse, offline: false };
  } catch {
    return { session: null, offline: true };
  }
}

let onExpired: () => void = () => undefined;
/** The app registers what to do when the session can't be renewed (clear caches, go to login). */
export const setSessionExpiredHandler = (handler: () => void) => {
  onExpired = handler;
};

/**
 * The one configured API client: base URL, bearer token, Accept-Language, CSRF header,
 * single-flight refresh on 401 and retry (packages/api-client).
 */
export const api = createApiClient({
  baseUrl: API_BASE_URL,
  // Late-bound so tests (and service-worker shims) can replace window.fetch.
  fetch: (input, init) => globalThis.fetch(input, init),
  getLocale: () => i18n.language,
  auth: {
    getAccessToken,
    async refresh() {
      const session = await refreshSession();
      if (!session) return null;
      useSession.getState().signIn(session.accessToken, session.user);
      return session.accessToken;
    },
    onSessionExpired: () => onExpired(),
  },
});
